// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import {
  type Task,
  type TaskAssigneeTaskInProgress,
  useEvolveGet,
  useEvolvePost,
  type User,
  type UserId,
  type WorkCellTask,
} from '@evolvemep/api-ts';
import { ActionIcon, Flex } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { useWrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { useUser } from 'hooks/user/useUser';
import { AssigneesAlreadyActiveModal } from 'modules/Shop/Tasks/ManageTaskTimeModal/AssigneesAlreadyActiveModal';
import { TimerModalAssignees } from 'modules/Shop/Tasks/ManageTaskTimeModal/TimerModalAssignees';
import { ResumeTaskModal } from 'modules/Shop/Tasks/TaskViewer/ResumeTaskModal';

type Props = {
  task: Task;
  onTaskUpdated: (t: Task) => void;
  workCellTask?: WorkCellTask;
  onClose: () => void;
};

/** Mantine's Modal.Header has `z-index: 1000` (hardcoded in ModalBase.css).
 *  This button overlaps the header via `translateY(-50%)`, so it needs the same z-index
 *  (DOM order ensures it renders on top at equal z-index). */
const MODAL_HEADER_Z_INDEX = 1000;

/**
 * Helper method to choose which assignees should be shown as active.
 * If the task is already started, active assignees are determined by the backend and we should show those.
 * If it's not started and the list has only one member, that member should be active.
 * If the task is not started and the list has more than one member, only the signed-in user should be active.
 */
const getDefaultAssignees = (task: Task, signedInUser: User) => {
  if (task.taskStatusTypeName === 'Started') {
    return task.assignees.filter(({ isActive }) => isActive);
  }
  if (task.assignees.length === 1) {
    return task.assignees;
  }
  return task.assignees.filter(({ user: { userId } }) => userId === signedInUser.userId);
};

/**
 * Renders the task start button and assignee list.
 * Should be rendered inside `ManageTaskTimeModal`.
 */
export const ManageTaskTimeControls = ({ task, workCellTask, onTaskUpdated, onClose }: Props) => {
  const { user: signedInUser } = useUser();
  const isStarted = task.taskStatusTypeName === 'Started';
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [blockingTasks, setBlockedAssignees] = useState<TaskAssigneeTaskInProgress[] | null>(null);
  const { setOnClose } = useWrappedModal();

  /** On mount, if the task is started, filter assignees by isActive. Otherwise, only the signed-in user will be active */
  const [activeUserIds, setActiveUserIds] = useState<UserId[]>(() =>
    getDefaultAssignees(task, signedInUser).map(({ user: { userId } }) => userId),
  );

  const { apiCall: checkInProgressApiCall, loading: checkingInProgress } = useEvolveGet(
    'shop/task/assignee/inProgressTasks',
  );
  const { apiCall: pauseApiCall, loading: pausing } = useEvolvePost(`shop/task/${task.taskId}/pause`);
  const { apiCall: startApiCall, loading: starting } = useEvolvePost(`shop/task/${task.taskId}/start`);

  const startTask = async () => {
    onTaskUpdated(await startApiCall({ activeUserIds }));
    /** Clear the "task was not started" toast if it was present */
    setOnClose(null);
  };

  const handleStart = async () => {
    if (task.taskStatusTypeName === 'Completed') {
      setShowResumePrompt(true);
      return;
    }
    await startTask();
  };

  const handleToggleTask = async () => {
    if (isStarted) {
      onTaskUpdated(await pauseApiCall({}));
      return;
    }
    /** Before starting the task,
     *  check if any of the active assignees are already performing tasks.
     *  If there are any, prompt the user before proceeding. */
    const retrievedBlockingTasks = await checkInProgressApiCall({ params: { otherThan: task.taskId } });
    const blockingTasksOnActiveAssignees = retrievedBlockingTasks.filter(({ user: { userId } }) =>
      activeUserIds.includes(userId),
    );
    if (blockingTasksOnActiveAssignees.length > 0) {
      setBlockedAssignees(blockingTasksOnActiveAssignees);
      return;
    }
    await handleStart();
  };

  const handleToggleAssignee = async (togglingUserId: UserId) => {
    if (!isStarted) {
      /** If the task isn't started, simply update the list. */
      setActiveUserIds((s) =>
        s.includes(togglingUserId) ? s.filter((id) => id !== togglingUserId) : [...s, togglingUserId],
      );
      /** Show a toast if the user toggled an assignee but hasn't started the task yet. */
      setOnClose(() => () => {
        notifications.show({
          title: 'Task not started',
          message: 'Active assignees were changed, but the task was not started.',
          color: 'yellow',
        });
        onClose();
      });
      return;
    }
    /** If the task is started, we need to tell the backend that we're updating the list of assignees. */
    const updatedTask = await startApiCall({
      activeUserIds: activeUserIds.includes(togglingUserId)
        ? activeUserIds.filter((userId) => userId !== togglingUserId)
        : [...activeUserIds, togglingUserId],
    });
    onTaskUpdated(updatedTask);
    const updatedActiveUserIds = updatedTask.assignees
      .filter(({ isActive }) => isActive)
      .map(({ user: { userId } }) => userId);
    setActiveUserIds(updatedActiveUserIds);
    if (updatedActiveUserIds.length === 0) {
      notifications.show({
        title: `You have turned off the only assignee working on ${task.taskName}.`,
        message: 'The task timer is still running!',
        color: 'yellow',
      });
    }
  };

  const isLoading = pausing || starting || checkingInProgress;
  return (
    <>
      <Flex flex={1} justify="center" style={{ transform: 'translateY(-50%)', zIndex: MODAL_HEADER_Z_INDEX }}>
        <ActionIcon
          variant={isStarted ? 'fancy-active' : 'fancy'}
          size={64}
          radius="100%"
          disabled={isLoading}
          loading={isLoading}
          onClick={handleToggleTask}
        >
          <EvolveIcon icon={isStarted ? 'Pause' : 'Play'} size={50} />
        </ActionIcon>
      </Flex>
      <TimerModalAssignees
        task={task}
        activeUserIds={activeUserIds}
        onToggleAssignee={handleToggleAssignee}
        onTaskUpdated={onTaskUpdated}
        loading={pausing || starting || checkingInProgress}
      />
      <AssigneesAlreadyActiveModal
        blockedAssignees={blockingTasks}
        onClose={() => setBlockedAssignees(null)}
        onConfirm={async () => await handleStart()}
      />
      <ResumeTaskModal
        task={task}
        workCellTask={workCellTask}
        /* Reaching this modal means the user pressed play, so finish the job they started rather than
           leaving the task paused. `handleStart` can't be reused here — `task` still reads Completed. */
        onTaskResumed={startTask}
        opened={showResumePrompt}
        onClose={() => setShowResumePrompt(false)}
      />
    </>
  );
};
